import { call, select } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import {
  cancelIngredientPurchaseOrder,
  cancelItemPurchaseOrder,
  fulfillIngredientPurchaseOrder,
  fulfillItemPurchaseOrder,
  postFulfilledIngredientPurchaseOrder,
  postFulfilledItemPurchaseOrder,
  postIngredientPurchaseOrder,
  postItemPurchaseOrder,
  putIngredientPurchaseOrder,
  putItemPurchaseOrder,
} from 'mf-shared/api/inventory/purchaseOrder/openApi';
import { NAMESPACE_ITEMS } from 'mf-shared/actions/inventory/constants';
import {
  IngredientPurchaseOrderRequest,
  ItemPurchaseOrderRequest,
  ShadowProxyUpdateIngredientPurchaseOrderRequestDto,
  ShadowProxyUpdateItemPurchaseOrderRequestDto,
} from '@ce-openapi/shadow-proxy-purchase-order-ts-axios-client';
import {
  CancelPOIngredientProps,
  CancelPOItemProps,
  CreatePOIngredientProps,
  CreatePOItemProps,
  FulfillPOIngredientProps,
  FulfillPOItemProps,
  PopulateDataIngredientRequestPayload,
  PopulateDataItemRequestPayload,
} from './types/purchaseOrder/form';
import generateShadowProxyItem from './utils/generateShadowProxyItem';
import generateShadowProxyIngredient from './utils/generateShadowProxyIngredient';
import {
  MokaState,
  PurchaseOrderState,
  Supplier,
} from './types/purchaseOrder/purchaseOrderState';

function* getSelectedSupplier(supplierId: number) {
  const suppliers: Supplier[] = yield select(
    (state: PurchaseOrderState) => state.inventory.purchaseOrder.suppliers
  );
  return suppliers.find((supplier) => supplierId === supplier.id);
}

function* generateIngredientPurchaseOrderData(
  payload: PopulateDataIngredientRequestPayload
) {
  const supplier = yield call(
    getSelectedSupplier,
    payload.ingredient_purchase_order.supplier_id
  );
  const ingredientPurchaseOrder: IngredientPurchaseOrderRequest = yield call(
    generateShadowProxyIngredient,
    payload,
    supplier?.supplier_id
  );
  return ingredientPurchaseOrder;
}

function* generateItemPurchaseOrderData(
  payload: PopulateDataItemRequestPayload
) {
  const supplier = yield call(
    getSelectedSupplier,
    payload.purchase_order.supplier_id
  );
  const itemPurchaseOrder: ItemPurchaseOrderRequest = yield call(
    generateShadowProxyItem,
    payload,
    supplier?.supplier_id
  );
  return itemPurchaseOrder;
}

export function* shadowApiCreatePOItem(
  payload: PopulateDataItemRequestPayload
) {
  const itemPurchaseOrder = yield call(generateItemPurchaseOrderData, payload);
  const result = yield call(
    postItemPurchaseOrder,
    payload.purchase_order.outlet_id,
    uuidv4(),
    {
      item_purchase_order_legacy: payload.purchase_order,
      item_purchase_order: itemPurchaseOrder,
    }
  );

  return result;
}

export function* shadowApiCreatePOIngredient(
  payload: PopulateDataIngredientRequestPayload
) {
  const ingredientPurchaseOrder: IngredientPurchaseOrderRequest = yield call(
    generateIngredientPurchaseOrderData,
    payload
  );
  const result = yield call(
    postIngredientPurchaseOrder,
    payload.ingredient_purchase_order.outlet_id,
    uuidv4(),
    {
      ingredient_purchase_order_legacy: payload.ingredient_purchase_order,
      ingredient_purchase_order: ingredientPurchaseOrder,
    }
  );

  return result;
}

export function* shadowApiCreatePO(
  props: CreatePOItemProps | CreatePOIngredientProps
) {
  const { namespace, payload } = props;

  const result =
    namespace === NAMESPACE_ITEMS
      ? yield call(shadowApiCreatePOItem, payload)
      : yield call(shadowApiCreatePOIngredient, payload);

  return result;
}

export function* shadowApiCreateFulfillPOItem(
  payload: PopulateDataItemRequestPayload
) {
  const itemPurchaseOrder = yield call(generateItemPurchaseOrderData, payload);
  const result = yield call(
    postFulfilledItemPurchaseOrder,
    payload.purchase_order.outlet_id,
    uuidv4(),
    {
      item_purchase_order_legacy: payload.purchase_order,
      item_purchase_order: itemPurchaseOrder,
    }
  );

  return result;
}

export function* shadowApiCreateFulfillPOIngredient(
  payload: PopulateDataIngredientRequestPayload
) {
  const ingredientPurchaseOrder: IngredientPurchaseOrderRequest = yield call(
    generateIngredientPurchaseOrderData,
    payload
  );
  const result = yield call(
    postFulfilledIngredientPurchaseOrder,
    payload.ingredient_purchase_order.outlet_id,
    uuidv4(),
    {
      ingredient_purchase_order_legacy: payload.ingredient_purchase_order,
      ingredient_purchase_order: ingredientPurchaseOrder,
    }
  );

  return result;
}

export function* shadowApiCreateFulfillPO(
  props: CreatePOItemProps | CreatePOIngredientProps
) {
  const { namespace, payload } = props;

  const result =
    namespace === NAMESPACE_ITEMS
      ? yield call(shadowApiCreateFulfillPOItem, payload)
      : yield call(shadowApiCreateFulfillPOIngredient, payload);

  return result;
}

function* shadowApiEditPOItem(payload: PopulateDataItemRequestPayload) {
  const itemPurchaseOrder = yield call(generateItemPurchaseOrderData, payload);
  const result = yield call(putItemPurchaseOrder, {
    outletId: payload.purchase_order.outlet_id,
    requestUuid: uuidv4(),
    purchaseOrderId: payload.purchase_order.external_transaction_uuid,
    payload: {
      item_purchase_order: itemPurchaseOrder,
      item_purchase_order_legacy: payload.purchase_order,
    },
  });
  return result;
}

function* shadowApiEditPOIngredient(
  payload: PopulateDataIngredientRequestPayload
) {
  const ingredientPurchaseOrder: IngredientPurchaseOrderRequest = yield call(
    generateIngredientPurchaseOrderData,
    payload
  );
  const result = yield call(putIngredientPurchaseOrder, {
    outletId: payload.ingredient_purchase_order.outlet_id,
    requestUuid: uuidv4(),
    purchaseOrderId:
      payload.ingredient_purchase_order.external_transaction_uuid,
    payload: {
      ingredient_purchase_order: ingredientPurchaseOrder,
      ingredient_purchase_order_legacy: payload.ingredient_purchase_order,
    },
  });
  return result;
}

export function* shadowApiEditPO(
  props: CreatePOItemProps | CreatePOIngredientProps
) {
  const { namespace, payload } = props;

  const result =
    namespace === NAMESPACE_ITEMS
      ? yield call(shadowApiEditPOItem, payload)
      : yield call(shadowApiEditPOIngredient, payload);

  return result;
}

export function* shadowApiFulfillPOItem(
  payload: ShadowProxyUpdateItemPurchaseOrderRequestDto
) {
  const itemPurchaseOrder: ItemPurchaseOrderRequest = {
    id: payload.external_transaction_uuid,
    is_advanced: false,
    note: payload.note,
    supplier_id: payload.supplier_uuid,
    order_no: payload.order_no,
    item_purchase_order_detail: payload.history_details.map((item) => ({
      ...item,
      begin_stock: item.beginning_stock,
      end_stock: item.end_stock,
      item_category_id: item.item.category_id,
      item_category_name: item.category_name,
      item_id: item.item_id,
      item_name: item.item_name,
      item_variant_id: item.item_variant_id,
      item_variant_name: item.item_variant_name,
      cost: item.price,
      quantity: item.quantity,
    })),
  };
  const result = yield call(fulfillItemPurchaseOrder, {
    outletId: payload.outlet_id!,
    requestUuid: payload.uuid,
    purchaseOrderId: payload.external_transaction_uuid,
    payload: {
      item_purchase_order: itemPurchaseOrder,
      item_purchase_order_legacy: payload,
    },
  });
  return result;
}

export function* shadowApiFulfillPOIngredient(
  payload: ShadowProxyUpdateIngredientPurchaseOrderRequestDto & {
    uuid: string;
  }
) {
  const ingredientPurchaseOrder: IngredientPurchaseOrderRequest = {
    id: payload.external_transaction_uuid,
    is_advanced: false,
    note: payload.note,
    order_no: payload.order_no,
    supplier_id: payload.supplier_uuid,
    ingredient_purchase_order_details: payload.ingredient_history_details.map(
      (ingredient) => ({
        end_stock: ingredient.end_stock,
        ingredient_category_id: ingredient.ingredient_category_id,
        ingredient_category_name: ingredient.ingredient_category_name,
        ingredient_id: ingredient.ingredient_id,
        ingredient_name: ingredient.ingredient_name,
        unit_id: ingredient.unit_id,
        unit_name: ingredient.unit_name,
        quantity: ingredient.quantity,
        begin_stock: ingredient.beginning_stock,
        unit_cost: ingredient.unit_price,
      })
    ),
  };
  const result = yield call(fulfillIngredientPurchaseOrder, {
    outletId: payload.outlet_id,
    requestUuid: payload.uuid,
    purchaseOrderId: payload.external_transaction_uuid,
    payload: {
      ingredient_purchase_order: ingredientPurchaseOrder,
      ingredient_purchase_order_legacy: payload,
    },
  });
  return result;
}

export function* shadowApiFulfillPO(
  props: FulfillPOItemProps | FulfillPOIngredientProps
) {
  const { namespace, payload } = props;

  const result =
    namespace === NAMESPACE_ITEMS
      ? yield call(shadowApiFulfillPOItem, payload)
      : yield call(shadowApiFulfillPOIngredient, payload);

  return result;
}

export function* shadowApiCancelPOItem(
  payload: ShadowProxyUpdateItemPurchaseOrderRequestDto
) {
  const result = yield call(cancelItemPurchaseOrder, {
    outletId: payload.outlet_id,
    requestUuid: uuidv4(),
    purchaseOrderId: payload.external_transaction_uuid,
    payload: {
      item_purchase_order_legacy: payload,
    },
  });

  return result;
}

export function* shadowApiCancelPOIngredient(
  payload: ShadowProxyUpdateIngredientPurchaseOrderRequestDto
) {
  const userId = yield select((state: MokaState) => state.moka.user.id);
  const result = yield call(cancelIngredientPurchaseOrder, {
    outletId: payload.outlet_id,
    requestUuid: uuidv4(),
    purchaseOrderId: payload.external_transaction_uuid,
    userId,
    payload: {
      ingredient_purchase_order_legacy: payload,
    },
  });

  return result;
}

export function* shadowApiCancelPO(
  props: CancelPOItemProps | CancelPOIngredientProps
) {
  const { namespace, payload } = props;

  const result =
    namespace === NAMESPACE_ITEMS
      ? yield call(shadowApiCancelPOItem, payload)
      : yield call(shadowApiCancelPOIngredient, payload);

  return result;
}
